import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { CloseIcon } from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { Sheet, SheetContent } from '../../../../../components/sheet/index';
import { useLocale } from '../../../../../hooks/index';
import { SignaturePopupType } from '../../enums/signature-popup-type.const';
import { SignaturePopupStore, ViewSignaturesMetadata } from '../../store/signature-popup.store';

export const SignaturesModal: FC = () => {
  const { messagesLocale } = useLocale();
  const isOpen = SignaturePopupStore(({ open }) => open);
  const type = SignaturePopupStore(({ type }) => type);
  const metadata = SignaturePopupStore(({ metadata }) => metadata);
  const handleDismiss = SignaturePopupStore(({ handleDismiss }) => handleDismiss);

  if (type !== SignaturePopupType.VIEW_SIGNATURES || !metadata) {
    return null;
  }

  if (!('signatories' in metadata) || !('documentName' in metadata)) {
    return null;
  }

  const { documentName, signatories }: ViewSignaturesMetadata = metadata;

  return (
    <Sheet onOpenChange={(isOpenState) => !isOpenState && handleDismiss()} open={isOpen}>
      <SheetContent
        className="sm:max-w-full flex max-h-[85vh] w-full flex-col gap-0 rounded-t-2xl p-0"
        side="BOTTOM"
      >
        <div className="flex shrink-0 items-center justify-between px-4 py-4">
          <h2 className="text-m font-semibold text-gray-700">{documentName}</h2>
          <Button
            className="flex size-7 items-center justify-center rounded-full border border-[#F2F2F2] bg-white shadow-[0_0_18.667px_0_rgba(0,0,0,0.08)]"
            onClick={handleDismiss}
            styleType="NONE"
          >
            <CloseIcon className="size-2" />
          </Button>
        </div>
        <div className="flex flex-1 flex-col gap-0 divide-y-[1px] overflow-y-auto px-4">
          {signatories.map((signatory) => (
            <div className="flex items-center gap-3 py-4" key={signatory.id}>
              <div className="relative shrink-0">
                <span
                  className={twMerge(
                    'flex size-10 items-center justify-center rounded-full text-sm font-medium',
                    signatory.isSigned
                      ? 'bg-forest-50 text-forest-600'
                      : 'bg-gray-100 text-gray-500',
                  )}
                >
                  {(signatory.stakeholder?.fullName?.[0] ?? '-').toUpperCase()}
                </span>
                <span
                  className={twMerge(
                    'absolute bottom-0 right-0 size-3 rounded-full border-2 border-white',
                    signatory.isSigned ? 'bg-forest-500' : 'bg-gray-300',
                  )}
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-gray-700">
                  {signatory.stakeholder?.fullName ?? '-'}
                </p>
                {signatory.stakeholder?.email && (
                  <p className="truncate text-label-sm text-gray-400">
                    {signatory.stakeholder.email}
                  </p>
                )}
              </div>
              {signatory.isSigned && (
                <span className="shrink-0 rounded-2xl bg-forest-50 px-2 py-1 text-sm text-forest-600">
                  {messagesLocale['SIGNED']}
                </span>
              )}
            </div>
          ))}
        </div>
        <div className="shrink-0 p-4">
          <Button className="w-full" onClick={handleDismiss} styleType="BLUE_ROUNDED">
            <AppFormattedMessage id="DONE" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
